import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Subscription, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { inflate } from 'zlib';

import {
  BATCH_CUTTING,
  HOLD,
  QUEUED,
  PRINT_READY,
  PRINTED,
  SHIPMENTS,
  HANDHELD,
  STATIONARY, 
  CUT,
  ID_CARD,
  ACCESSORY,
  RFID,
  LAMINATION
} from '../../ngrx/status-types';
import { JobLocation } from 'app/models';
import { allJobLocationFields } from 'ngrx/graphql';

const errorCodes = [400, 401, 403, 404];
@Injectable()
export class ScannerService {
  private _queue: string[];
  private _size : number;

  _batchCuttingListener: Subject<string>;
  _locations           : any;
  _types               : any;
  _locationToggle      : Subject<boolean>;
  _locationListener    : Subject<any>; 
  _qaScanListener      : Subject<string>;
  _laminationScanListener: Subject<string>;
  _accessoryScanListener: Subject<string>;
  _rfidScanListener    : Subject<string>;
  _printingScanListener : Subject<string>;
  _changeCutMachineScanListener : Subject<string>;
  _locationListening   : boolean;
  _idFound             : Observable<string>;
  _triggers            : string[];
  mainViewSubscription : Subscription;
  process              : string;
  private _exclusiveMode: boolean = false;

  constructor(
    private store: Store<any>,
    private credentialService: CredentialService,
    private authService: AuthenticationService,
  ) {
    this._queue    = [];
    this._size     = 1;
    this._triggers = [];

    this._locations = {
      '`': HOLD,
      '^': QUEUED,
      '~': PRINT_READY,
      '*': PRINTED,
      '<': SHIPMENTS,
      '+': BATCH_CUTTING,
      '#': CUT,
      '@': ID_CARD,
      '$': ACCESSORY,
      '&': RFID,
      '!': LAMINATION
    };

    this._types = {
      '%': HANDHELD,
      '>': STATIONARY
    };

    this._batchCuttingListener = new Subject<string>();
    this._locationListening    = false;
    this._locationListener     = new Subject<string>();
    this._qaScanListener       = new Subject<string>();
    this._laminationScanListener = new Subject<string>();
    this._accessoryScanListener= new Subject<string>();
    this._rfidScanListener     = new Subject<string>();
    this._printingScanListener = new Subject<string>();
    this._changeCutMachineScanListener = new Subject<string>();
    this._locationToggle       = new Subject<boolean>();
    this._idFound              = this._locationListener.asObservable();

    this.mainViewSubscription = this.store
      .select('main', 'mainView')
      .subscribe(({ process }) => {
        if (this.process !== process) {
          this._locationToggle.next(false);
          this.process = process;
        }
      });

    this._locationToggle
      .subscribe(listening => {
        if (!listening) {
          this._triggers = [];
          this._queue = [];
        }

        this._locationListening = listening;
      });

    this.trim();
  }

  get queue() {
    return this._queue;
  }

  get size() {
    return this._size;
  }

  push(char: string): void {
    // Append characters to end of queue
    this._queue.push(char);

    // If the queue exceeds the maximum size, remove excess characters
    if (this._queue.length > this._size) {
      this.trim();
    }

    // Listen for scanner input if location prefix detected
    if (this.listenerTrigger(this._locations)) {
      this._locationToggle.next(true);
    }

    // Listen for scanner input if job prefix detected
    // if (this.isEqualTo(this._jobIDStart) && !this._orderIDListening) {
    //   this._jobIDListening = true;
    // }
  }

  listenerTrigger(flags: any) {
    const trigger = this._queue[0];
    const found = flags[trigger];

    if (!found) return false;

    this._triggers.push(trigger);
    return true;
  }

  trim(): void {
    // Trim queue down to maximum size
    while (this._queue.length > this._size) {
      this._queue.shift();
    }
  }

  isEqualTo(array: string[]): boolean {
    if (this._queue.length !== array.length) {
      return false;
    } else {
      for (let i = 0; i < this._queue.length; i++) {
        if (this._queue[i] !== array[i]) {
          return false;
        }
      }
      return true;
    }
  }

  getJobLocationsByJobId(jobId: string): Observable<any> {
    const query = `
      {
        jobLocationsByJobId(jobId:"${jobId}"){
        ${allJobLocationFields}
      }
    }
    `
    return this.invokeJobLocations({ query });
  };

  getJobLocationsByID(ids: string[]): Observable<any> {
    let formattedIDs = '';
    ids.forEach(id => {
      formattedIDs += `"${id}", `;
    });
    const query = `
      {
        jobLocationsByID(ids: [${formattedIDs.trim()}]){
        ${allJobLocationFields}
      }
    }
    `
    return this.invokeJobLocations({ query });
  };

  getJobLocations(param: string, value: string): Observable<any> {
    const query = `
      {
        jobLocations(param:"${param}", value:"${value}"){
        ${allJobLocationFields}
      }
    }
    `
    return this.invokeJobLocations({ query });
  };

  getJobLocationsByPeriod(from: string, to: string, location: string): Observable<any> {
    const query = `
      {
        jobLocationsByPeriod(from:"${from}", to:"${to}", location:"${location}"){
        ${allJobLocationFields}
      }
    }
    `
    return this.invokeJobLocations({ query });
  };

  getJobLocationsLast24Hrs(): Observable<any> {
    const query = `
      {
        getJobLocationsLast24Hrs(param: ""){
        ${allJobLocationFields}
      }
    }
    `
    return this.invokeJobLocations({ query });
  };

  private invokeJobLocations(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.magentoFunction,
        Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
          }
          observer.error(err.stack);
        } else {
          const output = typeof data.Payload === 'string'
            ? JSON.parse(data.Payload)
            : data.Payload;
          if (output.gzip) {
            const options = {
              windowBits: 15
            };
            inflate(new Buffer(output.body, 'base64'), options, (err, data) => {
              if (err) {
                observer.error(err.stack);
              } else {
                const unzippedOutput = Object.assign({}, output, {
                  body: data.toString()
                });
                observer.next(unzippedOutput);
              }
            });
          } else {
            observer.next(output);
          }
        }
      }
      );
    });
  }

  getJobLocationsList(): Observable<JobLocation[]> {
    return this.store.select('jobLocations', 'jobLocationsList');
  }

  public setExclusiveMode(exclusive: boolean): void {
    this._exclusiveMode = exclusive;
    console.log(`Scanner exclusive mode ${exclusive ? 'enabled' : 'disabled'}`);
  }

  public isExclusiveMode(): boolean {
    return this._exclusiveMode;
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/services/scanner.service.ts